/** The arithmetic behind the circuit map (`TrackMap.tsx`): what each layer draws, and how the
 *  view moves.
 *
 * A published document carries more than a card's outline shows. The borders
 * are there, but so is the road surface between them, the spans the compiler
 * had to bridge without evidence, the metres that voted `wall`, the centre
 * line and the finish line — and a circuit's own page is where they are worth
 * looking at. Each is a layer, so a reader can take the road away to see the
 * gaps, or leave only the borders and read the shape.
 *
 * Everything is in the drawing space `TrackOutline` defines: world x, and
 * world z negated. Every layer is folded into a single path string. A circuit
 * is a thousand road quads and as many centre-line vertices, and a thousand
 * DOM nodes that pan together cost more than one node with a long `d`.
 *
 * The view is a viewBox and nothing else. Zooming scales it about the point
 * under the cursor, so the kerb being looked at stays under the cursor;
 * panning slides it; both are clamped against the fitted view, so the circuit
 * can be magnified forty times and cannot be lost off the edge of the card.
 * All of it is pure, and tested by feeding it numbers.
 */

import type { Point, ViewBox } from '@/components/TrackOutline';
import type { CentrelineVertex, GroundPoint, Segment, TrackCorner, TrackGeometry } from '@/lib/types';

export type MapLayer =
  | 'road'
  | 'borders'
  | 'gaps'
  | 'walls'
  | 'centerline'
  | 'finish'
  | 'corners'
  | 'sections';

export const MAP_LAYERS: ReadonlyArray<{ id: MapLayer; label: string; hint: string }> = [
  { id: 'road', label: 'Road', hint: 'the surface between the two borders, where both are surveyed' },
  { id: 'borders', label: 'Borders', hint: 'the left and right edges as they were driven' },
  { id: 'gaps', label: 'Gaps', hint: 'spans nobody has surveyed yet' },
  { id: 'walls', label: 'Walls', hint: 'metres of border marked as a wall' },
  { id: 'centerline', label: 'Centre line', hint: 'the middle of the road' },
  { id: 'finish', label: 'Start / finish', hint: 'averaged from recorded lap rollovers' },
  { id: 'corners', label: 'Corners', hint: 'each apex somebody marked, numbered from the start line' },
  { id: 'sections', label: 'Sections', hint: 'named stretches of road' },
];

export type LayerState = Record<MapLayer, boolean>;

/** What a reader sees first: the picture of the circuit and what is missing
 *  from it. The centre line is a construction line and starts switched off. */
export const DEFAULT_LAYERS: LayerState = {
  road: true,
  borders: true,
  gaps: true,
  walls: true,
  centerline: false,
  finish: true,
  corners: true,
  sections: true,
};

const fixed = (value: number): string => value.toFixed(2);

function finite(values: ReadonlyArray<number | null | undefined>): values is number[] {
  return values.every((value) => typeof value === 'number' && Number.isFinite(value));
}

/** `[x1, z1, x2, z2]` segments as one path of disjoint strokes. */
export function segmentsPath(segments: ReadonlyArray<Segment> | undefined): string {
  const parts: string[] = [];
  for (const segment of segments ?? []) {
    if (segment.length < 4 || !finite(segment)) continue;
    const [x1, z1, x2, z2] = segment;
    parts.push(`M ${fixed(x1)} ${fixed(-z1)} L ${fixed(x2)} ${fixed(-z2)}`);
  }
  return parts.join(' ');
}

/** The road's quads — `[x, z]` four times over — as one filled path. A quad
 *  that is short of eight numbers is skipped rather than drawn as a sliver. */
export function roadPath(road: ReadonlyArray<ReadonlyArray<number>> | undefined): string {
  const parts: string[] = [];
  for (const quad of road ?? []) {
    if (quad.length < 8 || !finite(quad.slice(0, 8))) continue;
    const corners: string[] = [];
    for (let index = 0; index < 8; index += 2) {
      corners.push(`${fixed(quad[index] as number)} ${fixed(-(quad[index + 1] as number))}`);
    }
    parts.push(`M ${corners.join(' L ')} Z`);
  }
  return parts.join(' ');
}

/** The centre line, one stroke per run. A run breaks wherever either border
 *  is unsurveyed, and joining two would draw a middle for a road nobody has
 *  measured the width of. */
export function centrelinePath(
  runs: ReadonlyArray<ReadonlyArray<CentrelineVertex>> | undefined,
): string {
  const parts: string[] = [];
  for (const run of runs ?? []) {
    const points = run.filter((vertex) => finite([vertex[0], vertex[1]]));
    if (points.length < 2) continue;
    parts.push(
      points
        .map(([x, z], index) => `${index === 0 ? 'M' : 'L'} ${fixed(x)} ${fixed(-z)}`)
        .join(' '),
    );
  }
  return parts.join(' ');
}

/** How many things each layer has to show, for the legend: a layer with
 *  nothing in it is offered disabled rather than as a switch that does
 *  nothing. */
export function layerCounts(geometry: TrackGeometry): Record<MapLayer, number> {
  const runs = (side: ReadonlyArray<unknown> | undefined) => side?.length ?? 0;
  return {
    road: geometry.road?.length ?? 0,
    borders: runs(geometry.borders.L) + runs(geometry.borders.R),
    gaps: runs(geometry.gaps?.L) + runs(geometry.gaps?.R),
    walls: geometry.walls?.length ?? 0,
    centerline: geometry.centerline?.length ?? 0,
    finish: geometry.finish ? 1 : 0,
    corners: geometry.corners?.length ?? 0,
    sections: geometry.sections?.length ?? 0,
  };
}

// ── corners ───────────────────────────────────────────────────────────────

/** `T7`, or `T7 Eau Rouge` once somebody has named it. */
export function cornerLabel(corner: Pick<TrackCorner, 'n' | 'name'>): string {
  return corner.name ? `T${corner.n} ${corner.name}` : `T${corner.n}`;
}

/** A ground position in the drawing's space: world x, world z negated. */
export function groundToPoint(position: GroundPoint): Point {
  return [position.x, -position.z];
}

/**
 * Where a label goes for something on the road at `at`: pushed `distance`
 * away from the middle of the circuit, so it sits outside the outline rather
 * than on the tarmac it is naming. It is a rule of thumb — an infield hairpin
 * gets its label pushed towards the next bit of road — but it never needs to
 * know which side of the road the apex is on, which an unlabelled corner does
 * not say. Something exactly at the middle is pushed straight up.
 */
export function labelPoint(at: Point, centre: Point, distance: number): Point {
  const dx = at[0] - centre[0];
  const dy = at[1] - centre[1];
  const length = Math.hypot(dx, dy);
  if (!(length > 0)) return [at[0], at[1] - distance];
  return [at[0] + (dx / length) * distance, at[1] + (dy / length) * distance];
}

/** The middle of a view, which for the fitted view is the middle of the
 *  circuit. */
export function viewCentre(view: ViewBox): Point {
  return [view.x + view.width / 2, view.y + view.height / 2];
}

/** Names crowd a whole circuit at card size — Spa's Les Combes is three
 *  corners in eighty metres — so the numbers are always drawn and the names
 *  arrive once the map has been magnified enough to have room for them. */
export const NAME_ZOOM = 2.5;

// ── the view ──────────────────────────────────────────────────────────────

/** How far in the map goes: at forty times a 5 km circuit shows about 150 m
 *  across, which is a corner — past that the survey's one-metre records are
 *  the only thing left to look at. */
export const MAX_ZOOM = 40;

/**
 * Widen the fitted view to the element's own proportions.
 *
 * The browser letterboxes a viewBox that does not match its element, and then
 * "a third of the way across the element" is no longer a third of the way
 * across the view — the cursor and the point it is zooming about drift apart.
 * A view with the element's proportions has no letterbox, so a fraction of
 * one is the same fraction of the other.
 */
export function fillAspect(view: ViewBox, elementWidth: number, elementHeight: number): ViewBox {
  if (!(elementWidth > 0 && elementHeight > 0 && view.width > 0 && view.height > 0)) return view;
  const aspect = elementWidth / elementHeight;
  if (view.width / view.height < aspect) {
    const width = view.height * aspect;
    return { ...view, x: view.x - (width - view.width) / 2, width };
  }
  const height = view.width / aspect;
  return { ...view, y: view.y - (height - view.height) / 2, height };
}

/** How many times the fitted view has been magnified. */
export function zoomOf(view: ViewBox, base: ViewBox): number {
  return view.width > 0 ? base.width / view.width : 1;
}

/** Keep the view's centre inside the fitted view, so the circuit can be slid
 *  to the edge of the card and never past it. */
export function clampView(view: ViewBox, base: ViewBox): ViewBox {
  const clamp = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high);
  const centreX = clamp(view.x + view.width / 2, base.x, base.x + base.width);
  const centreY = clamp(view.y + view.height / 2, base.y, base.y + base.height);
  return { ...view, x: centreX - view.width / 2, y: centreY - view.height / 2 };
}

/**
 * Magnify by `factor` about an anchor given as fractions of the view — `0.5,
 * 0.5` is its centre, and the cursor's position over the map is whatever
 * fraction of the element it sits at. The world point under the anchor is
 * under it afterwards too, until the clamp has to move it.
 */
export function zoomAt(
  view: ViewBox,
  base: ViewBox,
  factor: number,
  anchorX = 0.5,
  anchorY = 0.5,
): ViewBox {
  if (!Number.isFinite(factor) || factor <= 0) return view;
  const target = Math.min(Math.max(zoomOf(view, base) * factor, 1), MAX_ZOOM);
  if (target === 1) return base;

  const width = base.width / target;
  const height = base.height / target;
  const worldX = view.x + view.width * anchorX;
  const worldY = view.y + view.height * anchorY;
  return clampView(
    { x: worldX - width * anchorX, y: worldY - height * anchorY, width, height },
    base,
  );
}

/** The view at `zoom` times, centred on a point: what picking a corner from
 *  the list does to the map. */
export function focusOn(base: ViewBox, at: Point, zoom: number): ViewBox {
  const target = Math.min(Math.max(zoom, 1), MAX_ZOOM);
  if (target === 1) return base;
  const width = base.width / target;
  const height = base.height / target;
  return clampView({ x: at[0] - width / 2, y: at[1] - height / 2, width, height }, base);
}

/** Slide the view by a fraction of itself: `0.25, 0` is a quarter of a screen
 *  to the right. Dragging passes the pointer's travel as that fraction. */
export function panBy(view: ViewBox, base: ViewBox, fractionX: number, fractionY: number): ViewBox {
  if (zoomOf(view, base) <= 1) return view;
  return clampView(
    { ...view, x: view.x + view.width * fractionX, y: view.y + view.height * fractionY },
    base,
  );
}

// ── the scale bar ─────────────────────────────────────────────────────────

export interface ScaleBar {
  metres: number;
  /** The bar's length on screen. */
  pixels: number;
}

const SCALE_STEPS = [1, 2, 5];

/**
 * A round number of metres that draws at no more than `maxPixels`.
 *
 * The SVG fits its viewBox inside the element and keeps its proportions, so
 * a metre is as many pixels as the tighter of the two axes allows — the same
 * `min` the browser takes for `preserveAspectRatio="xMidYMid meet"`.
 */
export function scaleBar(
  view: ViewBox,
  elementWidth: number,
  elementHeight: number,
  maxPixels = 120,
): ScaleBar | null {
  if (!(view.width > 0 && view.height > 0 && elementWidth > 0 && elementHeight > 0)) return null;
  const pixelsPerMetre = Math.min(elementWidth / view.width, elementHeight / view.height);
  if (!(pixelsPerMetre > 0)) return null;

  const limit = maxPixels / pixelsPerMetre;
  let best = 0;
  for (let power = 0; power <= 5; power += 1) {
    for (const step of SCALE_STEPS) {
      const metres = step * 10 ** power;
      if (metres <= limit && metres > best) best = metres;
    }
  }
  if (best === 0) return null;
  return { metres: best, pixels: best * pixelsPerMetre };
}

export function scaleLabel(metres: number): string {
  if (metres >= 1000) return `${metres / 1000} km`;
  return `${metres} m`;
}
